<template>
  <Teleport to="#modalTarget">
    <div v-if="show">
      <div class="modal-backdrop show"></div>
      <div class="modal d-block" tabindex="-1">
        <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Passwort ändern</h5>
          <button
            type="button"
            class="btn-close"
            @click="$emit('close')"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <div v-if="success" class="alert alert-success">
            Passwort wurde erfolgreich geändert
          </div>
          <div v-else>
            <div class="alert alert-gradient-warning">
              <strong>Wichtiger Hinweis:</strong> Nach der Passwort-Änderung wirst du auf allen Geräten ausgeloggt und musst dich dort erneut anmelden.
            </div>
            <p>
              Bitte gib dein neues Passwort ein. Es muss mindestens 8 Zeichen lang sein.
            </p>
            <div class="mb-3">
              <label for="newPassword" class="form-label d-flex align-items-center">
                Neues Passwort 
                <span class="badge bg-danger ms-2">Pflichtfeld</span>
              </label>
              <div class="input-group">
                <input
                  :type="showPassword ? 'text' : 'password'"
                  class="form-control"
                  id="newPassword"
                  v-model="password"
                  placeholder="Neues Passwort eingeben"
                  minlength="8"
                  @input="onPasswordChange"
                  :class="{ 'is-invalid': passwordError }"
                  tabindex="201"
                />
                <button
                  type="button"
                  class="btn btn-outline-secondary"
                  @click="togglePasswordVisibility"
                  tabindex="204"
                >
                  <font-awesome-icon :icon="showPassword ? 'eye-slash' : 'eye'" />
                </button>
              </div>
              <div v-if="passwordError" class="invalid-feedback d-block">
                {{ passwordError }}
              </div>

              <div v-if="password" class="mt-2">
                <div class="d-flex justify-content-between">
                  <small>Passwortstärke:</small>
                  <small :class="getTextColorClass(passwordStrength.class)">
                    {{
                      passwordStrength.class === 'weak'
                        ? 'Schwach'
                        : passwordStrength.class === 'medium'
                          ? 'Mittel'
                          : passwordStrength.class === 'strong'
                            ? 'Stark'
                            : passwordStrength.class === 'veryStrong'
                              ? 'Sehr stark'
                              : ''
                    }}
                  </small>
                </div>
                <div class="progress quitly-progress-thin">
                  <div
                    class="progress-bar quitly-progress-animated"
                    :class="getProgressBarClass(passwordStrength.class)"
                    role="progressbar"
                    :style="{ width: passwordStrength.percent + '%' }"
                    :aria-valuenow="passwordStrength.percent"
                    aria-valuemin="0"
                    aria-valuemax="100"
                  ></div>
                </div>
                <div class="mt-1">
                  <small>
                    Das Passwort muss mindestens 8 Zeichen lang sein und Groß- und
                    Kleinbuchstaben, Zahlen und Sonderzeichen enthalten.
                  </small>
                </div>
              </div>
            </div>

            <div class="mb-3">
              <label for="confirmPassword" class="form-label d-flex align-items-center">
                Passwort bestätigen 
                <span class="badge bg-danger ms-2">Pflichtfeld</span>
              </label>
              <div class="input-group">
                <input
                  :type="showConfirmPassword ? 'text' : 'password'"
                  class="form-control"
                  id="confirmPassword"
                  v-model="confirmPassword"
                  placeholder="Passwort wiederholen"
                  :class="{ 'is-invalid': confirmPasswordError }"
                  minlength="8"
                  @input="validateConfirmPassword"
                  tabindex="202"
                />
                <button
                  type="button"
                  class="btn btn-outline-secondary"
                  @click="toggleConfirmPasswordVisibility"
                  tabindex="205"
                >
                  <font-awesome-icon :icon="showConfirmPassword ? 'eye-slash' : 'eye'" />
                </button>
              </div>
              <div v-if="confirmPasswordError" class="invalid-feedback d-block">
                {{ confirmPasswordError }}
              </div>
            </div>

            <div v-if="error" class="alert alert-danger">
              {{ error }}
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            @click="$emit('close')"
            tabindex="206"
          >
            {{ success ? 'Schließen' : 'Abbrechen' }}
          </button>
          <button
            v-if="!success"
            type="button"
            class="btn btn-primary"
            @click="handleSubmit"
            :disabled="loading || !isPasswordValid"
            tabindex="203"
          >
            {{ loading ? 'Wird gesendet...' : 'Passwort aktualisieren' }}
          </button>
        </div>
        </div>
      </div>
    </div>
  </div></Teleport>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close', 'submit'])

const password = ref('')
const confirmPassword = ref('')
const passwordError = ref('')
const confirmPasswordError = ref('')
const error = ref('')
const success = ref(false)
const loading = ref(false)
const showPassword = ref(false)
const showConfirmPassword = ref(false)

const passwordStrength = ref({
  class: 'none',
  percent: 0
})

const isPasswordValid = computed(() => {
  if (!password.value || !confirmPassword.value) {
    return false
  }
  

  const passwordVal = password.value
  
  if (passwordVal.length < 8) {
    return false
  }
  
  const hasUpperCase = /[A-Z]/.test(passwordVal)
  const hasLowerCase = /[a-z]/.test(passwordVal)
  const hasNumbers = /[0-9]/.test(passwordVal)
  const hasSpecialChars = /[^A-Za-z0-9]/.test(passwordVal)
  
  return (
    hasUpperCase &&
    hasLowerCase &&
    hasNumbers &&
    hasSpecialChars &&
    password.value === confirmPassword.value
  )
})

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value
}

const toggleConfirmPasswordVisibility = () => {
  showConfirmPassword.value = !showConfirmPassword.value
}

const onPasswordChange = () => {
  validatePassword()
  if (confirmPassword.value) {
    validateConfirmPassword()
  }
}

const validatePassword = () => {
  passwordError.value = ''
  const passwordVal = password.value

  if (!passwordVal) {
    passwordStrength.value = { class: 'none', percent: 0 }
    return false
  }

  if (passwordVal.length < 8) {
    passwordError.value = 'Das Passwort muss mindestens 8 Zeichen lang sein'
    passwordStrength.value = { class: 'weak', percent: 25 }
    return false
  }


  const hasUpperCase = /[A-Z]/.test(passwordVal)
  const hasLowerCase = /[a-z]/.test(passwordVal)
  const hasNumbers = /[0-9]/.test(passwordVal)
  const hasSpecialChars = /[^A-Za-z0-9]/.test(passwordVal)

  const missingCriteria = []
  if (!hasUpperCase) missingCriteria.push('Großbuchstaben')
  if (!hasLowerCase) missingCriteria.push('Kleinbuchstaben') 
  if (!hasNumbers) missingCriteria.push('Zahlen')
  if (!hasSpecialChars) missingCriteria.push('Sonderzeichen')

  if (missingCriteria.length > 0) {
    passwordError.value = `Das Passwort muss enthalten: ${missingCriteria.join(', ')}`
    passwordStrength.value = { class: 'weak', percent: 25 }
    return false
  }


  let strength = 0

  if (passwordVal.length >= 8) strength += 1
  if (passwordVal.length >= 12) strength += 1

  if (hasUpperCase) strength += 1
  if (hasLowerCase) strength += 1
  if (hasNumbers) strength += 1
  if (hasSpecialChars) strength += 1

  if (strength <= 2) {
    passwordStrength.value = { class: 'weak', percent: 25 }
  } else if (strength <= 4) {
    passwordStrength.value = { class: 'medium', percent: 50 }
  } else if (strength <= 5) {
    passwordStrength.value = { class: 'strong', percent: 75 }
  } else {
    passwordStrength.value = { class: 'veryStrong', percent: 100 }
  }

  return true
}

const validateConfirmPassword = () => {
  confirmPasswordError.value = ''

  if (confirmPassword.value && password.value !== confirmPassword.value) {
    confirmPasswordError.value = 'Die Passwörter stimmen nicht überein'
  }
}

const getProgressBarClass = (strengthClass) => {
  switch (strengthClass) {
    case 'weak': return 'bg-danger'
    case 'medium': return 'bg-warning'
    case 'strong': return 'bg-info'
    case 'veryStrong': return 'bg-success'
    default: return ''
  }
}

const getTextColorClass = (strengthClass) => {
  switch (strengthClass) {
    case 'weak': return 'text-danger'
    case 'medium': return 'text-warning'
    case 'strong': return 'text-info'
    case 'veryStrong': return 'text-success'
    default: return ''
  }
}

const handleSubmit = async () => {
  passwordError.value = ''
  confirmPasswordError.value = ''
  error.value = ''

  const isPasswordValid = validatePassword()
  validateConfirmPassword()

  if (!isPasswordValid || passwordError.value || confirmPasswordError.value) {
    return
  }

  if (!password.value) {
    passwordError.value = 'Passwort ist erforderlich'
    return
  }

  if (password.value !== confirmPassword.value) {
    confirmPasswordError.value = 'Die Passwörter stimmen nicht überein'
    return
  }

  loading.value = true

  try {
    await emit('submit', password.value)
    success.value = true


    setTimeout(() => {
      emit('close')
      reset()
    }, 3000)
  } catch (err) {

    console.error('Error in password change:', err)
  } finally {
    loading.value = false
  }
}

const reset = () => {
  password.value = ''
  confirmPassword.value = ''
  passwordError.value = ''
  confirmPasswordError.value = ''
  error.value = ''
  success.value = false
  loading.value = false
  showPassword.value = false
  showConfirmPassword.value = false
  passwordStrength.value = { class: 'none', percent: 0 }
}


watch(() => props.show, (newShow) => {
  if (!newShow) {
    reset()
  }
})


defineExpose({
  setError: (message) => {
    error.value = message
    loading.value = false
  },
  setSuccess: () => {
    success.value = true
    loading.value = false
  },
  setLoading: (state) => {
    loading.value = state
  }
})
</script>
